import { FC, memo } from 'react'
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native'
import { OrderStatesEnum } from '../../constants/order'
import { getShortOrderCode } from '../../helpers/getShortOrderCode'
import { getUpdateStateButtonText } from '../../helpers/getUpdateStateButtonText'
import { handleNextOrderState } from '../../helpers/handleNextOrderState'
import { useAppSelector } from '../../hooks/redux'
import { appSelectors } from '../../redux/reducers/app'
import { orderSelectors } from '../../redux/reducers/order'
import { stationSelectors } from '../../redux/reducers/station'
import { IOrder } from '../../types/order'
import DividedList from '../UI/DividedList'
import Note from '../UI/OrderCard/Note'
import OrderCard from '../UI/OrderCard/OrderCard'
import ZoneBar from '../UI/OrderCard/ZoneBar'
import OrderInfoBar from './OrderInfoBar'
import Product from './Product'
import UpdateStateButton from './UpdateStateButton'

const Order: FC<IOrder & { style?: StyleProp<ViewStyle> }> = ({
    orderKey,
    isAddressRequired,
    code,
    dateTime,
    customer,
    zone,
    products,
    source,
    stations,
    state,
    note,
    style,
}) => {
    const { code: stationCode, id, type } = useAppSelector(stationSelectors.currentStation)!
    const isArchive = useAppSelector(appSelectors.isArchive)
    const color = useAppSelector(orderSelectors.orderColor(orderKey))

    const orderKitchenState = stations?.[stationCode]

    const handlePress =
        orderKitchenState &&
        (() =>
            handleNextOrderState(id, type, orderKey, orderKitchenState, state, isAddressRequired))

    const shortCode = getShortOrderCode(code)

    const withUpdateStateButton =
        (orderKitchenState === OrderStatesEnum.NEW ||
            state === OrderStatesEnum.COOKED ||
            state === OrderStatesEnum.ASSEMBLED) &&
        !isArchive

    const buttonText =
        withUpdateStateButton && orderKitchenState
            ? getUpdateStateButtonText(
                  id,
                  type,
                  orderKey,
                  orderKitchenState,
                  state,
                  isAddressRequired
              )
            : ''

    const inStationProducts = products.filter(
        (product) => product.digibonStation && product.digibonStation === stationCode
    )
    const outStationProducts = products.filter(
        (product) => product.digibonStation && product.digibonStation !== stationCode
    )

    const zoneBarAddress = [
        ...(customer.street ? [customer.street] : []),
        ...(customer.town ? [customer.town] : []),
    ].join(', ')

    return (
        <OrderCard style={[styles.container, style]}>
            <OrderInfoBar
                isAddressRequired={isAddressRequired}
                code={shortCode}
                dateTime={dateTime}
                customer={customer}
                source={source}
                orderKey={orderKey}
            />
            {isAddressRequired && zone.name && (
                <ZoneBar
                    zoneName={zone.name}
                    address={zoneBarAddress}
                    driverName={zone.driverName}
                />
            )}
            <View style={{ backgroundColor: color }}>
                <DividedList
                    upperItems={inStationProducts.map((product) => (
                        <Product key={product.id} {...product} />
                    ))}
                    lowerItems={outStationProducts.map((product) => (
                        <Product key={product.id} {...product} />
                    ))}
                />
                {!!note && note.length > 0 && (
                    <Note style={{ marginBottom: 16, marginHorizontal: 16 }}>{note}</Note>
                )}
            </View>
            {withUpdateStateButton && handlePress && (
                <UpdateStateButton onPress={handlePress}>{buttonText}</UpdateStateButton>
            )}
        </OrderCard>
    )
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 24,
    },
})

export default memo(
    Order,
    (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps)
)
